import { useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import {
    useCommunication,
    useDeleteCommunication,
    useUpdateCommunication
} from '../../hooks/useCommunications';
import { usePracticeDetail } from '../../hooks/usePracticeDetail';
import { usePracticeOptions } from '../../hooks/useBilling';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useToast } from '../../providers/ToastProvider';
import { apiErrorMessage } from '../../utils/apiError';
import { practiceOptionsFromList } from '../../utils/practiceFilterOptions';
import type {
    CommunicationPatientRef,
    CommunicationStatus,
    UpdateCommunicationInput
} from '../../services/communications';
import { CommunicationDetailHeader } from '../../components/accounts/practice/communications/CommunicationDetailHeader';
import { CommunicationIdentityCard } from '../../components/accounts/practice/communications/CommunicationIdentityCard';
import { CommunicationMetaRail } from '../../components/accounts/practice/communications/CommunicationMetaRail';
import { CommunicationTimeline } from '../../components/accounts/practice/communications/CommunicationTimeline';
import { DeleteCommunicationConfirmModal } from '../../components/accounts/practice/communications/DeleteCommunicationConfirmModal';
import {
    useOpenCommunicationsCount,
    useRecentCommunications
} from '../../components/accounts/practice/communications/useRecentCommunications';

function CenteredSpinner() {
    return (
        <div className="flex-1 flex items-center justify-center">
            <div className="w-8 h-8 rounded-full border-2 border-gray-300 border-t-gray-800 animate-spin" />
        </div>
    );
}

// Reachable both practice-scoped and practice-less, same component either
// way - the owning practice comes from the fetched thread, not the URL.
export function CommunicationDetailPage() {
    const { communicationId } = useParams<{ communicationId: string }>();
    const navigate = useNavigate();
    const { showToast } = useToast();

    const { communication, isLoading, isError, refetch } = useCommunication(communicationId);
    const { practice } = usePracticeDetail(communication?.practiceId ?? undefined);
    const { practices: practiceOptionsList, isLoading: isPracticeOptionsLoading } =
        usePracticeOptions(!communication?.practiceId);
    const { totalCommunications } = useRecentCommunications(
        communication?.practiceId ?? null,
        communication?.id
    );
    const openCommunicationsCount = useOpenCommunicationsCount(communication?.practiceId ?? null);

    const updateCommunication = useUpdateCommunication();
    const deleteCommunication = useDeleteCommunication();
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    usePageTitle(communication ? `${communication.title} · ${communication.referenceNo}` : null);

    if (!communicationId) {
        return <Navigate to="/accounts/communications" replace />;
    }

    if (isLoading) {
        return <CenteredSpinner />;
    }

    if (isError || !communication) {
        return (
            <div className="flex-1 flex flex-col items-center justify-center gap-3">
                <div className="text-sm text-red-500">Failed to load this communication</div>
                <button
                    onClick={() => refetch()}
                    className="px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                    Retry
                </button>
            </div>
        );
    }

    const returnPath = communication.practiceId
        ? `/accounts/practices/${communication.practiceId}?tab=communications`
        : '/accounts/communications';

    const patch = (changes: UpdateCommunicationInput, failureMessage: string) => {
        updateCommunication.mutate(
            { id: communication.id, patch: changes },
            { onError: (err) => showToast(apiErrorMessage(err, failureMessage), 'error') }
        );
    };

    const handleTitleSave = (title: string) => patch({ title }, 'Failed to update title');
    const handleStatusChange = (status: CommunicationStatus) =>
        patch({ status }, 'Failed to update status');
    const handleAssign = (assigneeEmail: string) =>
        patch({ assigneeEmail }, 'Failed to assign communication');
    const handleUnassign = () => patch({ assigneeEmail: null }, 'Failed to unassign communication');
    const handlePracticeChange = (practiceId: string) =>
        patch({ practiceId }, 'Failed to attach practice');
    const handlePatientsChange = (patients: CommunicationPatientRef[]) =>
        patch({ patientIds: patients.map((p) => p.id) }, 'Failed to update linked patients');
    const handleCategoriesChange = (categories: string[]) =>
        patch({ categories }, 'Failed to update categories');

    const handleConfirmDelete = () => {
        deleteCommunication.mutate(
            { id: communication.id, practiceId: communication.practiceId },
            {
                onSuccess: () => {
                    showToast('Communication deleted', 'success');
                    navigate(returnPath);
                },
                onError: (err) =>
                    showToast(apiErrorMessage(err, 'Failed to delete communication'), 'error')
            }
        );
    };

    return (
        <div className="flex flex-col h-full overflow-hidden bg-gray-100">
            <CommunicationDetailHeader
                communication={communication}
                practiceName={practice?.name ?? communication.practiceName}
                onNavigate={navigate}
            />

            {/* No page-level scroll here anymore (overflow-hidden, not
                overflow-y-auto) - the identity card, this heading row's
                sibling toolbar, and the rail all stay put; only
                CommunicationTimeline's own interior list scrolls. Both
                columns need `h-full` for that split (and the rail's
                already-independent scroll) to actually have a determined
                height to work against. */}
            <div className="flex-1 min-h-0 px-8 pb-8 mt-2 overflow-hidden">
                <div className="h-full flex items-start gap-5">
                    <div className="flex-1 min-w-0 h-full flex flex-col gap-8">
                        <div className="shrink-0">
                            <CommunicationIdentityCard
                                title={communication.title}
                                referenceNo={communication.referenceNo}
                                createdAt={communication.createdAt}
                                isSaving={updateCommunication.isPending}
                                onTitleSave={handleTitleSave}
                            />
                        </div>
                        <CommunicationTimeline
                            communicationId={communication.id}
                            communicationTitle={communication.title}
                            practiceId={communication.practiceId}
                            entries={communication.entries}
                            linkedPatients={communication.patients}
                        />
                    </div>
                    <CommunicationMetaRail
                        communication={communication}
                        practice={practice ?? null}
                        practiceOptions={practiceOptionsFromList(practiceOptionsList)}
                        isPracticeOptionsLoading={isPracticeOptionsLoading}
                        totalCommunications={totalCommunications}
                        openCommunicationsCount={openCommunicationsCount}
                        isSaving={updateCommunication.isPending}
                        onPracticeChange={handlePracticeChange}
                        onPatientsChange={handlePatientsChange}
                        onCategoriesChange={handleCategoriesChange}
                        onStatusChange={handleStatusChange}
                        onAssign={handleAssign}
                        onUnassign={handleUnassign}
                        onDeleteClick={() => setShowDeleteModal(true)}
                    />
                </div>
            </div>

            {showDeleteModal && (
                <DeleteCommunicationConfirmModal
                    communication={communication}
                    isDeleting={deleteCommunication.isPending}
                    onConfirm={handleConfirmDelete}
                    onCancel={() => setShowDeleteModal(false)}
                />
            )}
        </div>
    );
}
